import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiClient } from '../../services/api';
import { SurveyTemplateSummary } from '../../types/survey';

const TemplateManagement: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [templates, setTemplates] = useState<SurveyTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getSurveyTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId: string, title: string) => {
    if (!window.confirm(`「${title}」${t('template', 'deleteConfirm')}`)) return;
    try {
      await apiClient.deleteSurveyTemplate(templateId);
      setTemplates(prev => prev.filter(t => t.templateId !== templateId));
    } catch (err) {
      alert(t('common', 'deleteFailed'));
    }
  };

  const typeLabel = (type: string) => {
    switch (type) {
      case 'current_state': return t('template', 'typeCurrentState');
      case 'future_vision': return t('template', 'typeFutureVision');
      default: return type;
    }
  };

  return (
    <div style={{ padding: '30px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>{t('template', 'management')}</h2>
        <button
          onClick={() => navigate('/admin/templates/upload')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#2c5282',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.95rem'
          }}
        >
          {t('template', 'csvUpload')}
        </button>
      </div>

      {loading ? (
        <p>{t('common', 'loading')}</p>
      ) : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ color: '#718096', fontSize: '1.1rem' }}>{t('template', 'noTemplates')}</p>
          <p style={{ color: '#a0aec0' }}>{t('template', 'uploadPrompt')}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {templates.map(tmpl => (
            <div key={tmpl.templateId} style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: '0 0 6px', fontSize: '1.1rem' }}>{tmpl.title}</h3>
                <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem', color: '#718096' }}>
                  <span style={{
                    padding: '2px 8px',
                    backgroundColor: tmpl.type === 'current_state' ? '#ebf8ff' : '#faf5ff',
                    color: tmpl.type === 'current_state' ? '#2c5282' : '#6b46c1',
                    borderRadius: '4px',
                    fontSize: '0.8rem'
                  }}>
                    {typeLabel(tmpl.type)}
                  </span>
                  <span>{tmpl.totalQuestions}{t('common', 'questions')}</span>
                  <span>{tmpl.categories?.length || 0}{t('common', 'categories')}</span>
                  <span>{new Date(tmpl.createdAt).toLocaleDateString('ja-JP')}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {!tmpl.templateId.startsWith('questionnaire:') && (
                  <button
                    onClick={() => navigate(`/admin/templates/${tmpl.templateId}/edit`)}
                    style={{
                      padding: '6px 14px',
                      backgroundColor: '#ebf8ff',
                      color: '#2c5282',
                      border: '1px solid #bee3f8',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    {t('common', 'edit')}
                  </button>
                )}
                <button
                  onClick={() => handleDelete(tmpl.templateId, tmpl.title)}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: '#fff5f5',
                    color: '#e53e3e',
                    border: '1px solid #fed7d7',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  {t('common', 'delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: '20px' }}>
        <button
          onClick={() => navigate('/admin/surveys')}
          style={{ padding: '8px 16px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#4a5568' }}
        >
          {t('template', 'goToSurveys')}
        </button>
      </div>
    </div>
  );
};

export default TemplateManagement;
